import { Link } from '@material-ui/core';
import { forwardRef, ForwardRefRenderFunction, useState } from 'react';

import ROUTES from '@agency/routes/routes.constant';
import { companiesQueryKeys } from '@apis/companiesApi';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { AutocompleteRef } from '@shared/components/autocomplete';
import { Button } from '@shared/components/button-new';
import { Lookup, LookupProps } from '@shared/components/lookups/lookup';
import { Company, MixedCompany, NewCompany } from '@shared/models/company/company';
import { InputSize } from '@shared/types/common/input';
import { getQueriesAsSearch } from '@shared/utils/common';

import { CompanyLookupCard } from './company-lookup-card';
import { styles } from './CompanyLookup.styles';

export const isCompanyValueSame = (item: MixedCompany, value: MixedCompany) => {
  if ('id' in item && 'id' in value) {
    return item.id === value.id;
  }
  if ('name' in item && 'name' in value) {
    return item.name === value.name;
  }
  return false;
};

// ******************************************************
// * !!        DO NOT USE COMPONENT DIRECTLY         !! *
// * !! USE CompanySingleLookup / CompanyMultiLookup !! *
// ******************************************************

export interface CompanyLookupProps
  extends Omit<
      LookupProps,
      | 'classes'
      | 'localSearch'
      | 'placeholder'
      | 'getActionRow'
      | 'onCreate'
      | 'queryFactory'
      | 'renderOption'
      | 'setLocalSearch'
    >,
    WithStyles<typeof styles> {
  dnbSearch?: boolean;
  initialSearch?: string;
  investment?: boolean;
  lookupClasses?: Partial<LookupProps['classes']>;
  placeholder?: string;
  onCreate?: (value: NewCompany) => void;
}

const CompanyLookupComponent: ForwardRefRenderFunction<AutocompleteRef, CompanyLookupProps> = (
  { classes, dnbSearch, initialSearch, investment = false, lookupClasses, onCreate, onSelect, ...lookupProps },
  ref
) => {
  const [localSearch, setLocalSearch] = useState<string>(() => initialSearch || '');

  return (
    <Lookup
      {...lookupProps}
      classes={lookupClasses}
      localSearch={localSearch}
      placeholder={t('xtextx_ellipsis', { text: lookupProps.placeholder || t('search_for_a_company') })}
      ref={ref}
      getActionRow={(search, clearSearch) => {
        if (!search || !onCreate) {
          return undefined;
        }

        if (dnbSearch) {
          return (
            <Link
              href={`${ROUTES.organisation.crm.root}${ROUTES.organisation.crm.companies}${getQueriesAsSearch({
                create: true,
                company_name: search,
              })}`}
              target="_blank"
            >
              <Button text={`${t('create_company')} “${search}”`} />
            </Link>
          );
        }

        return (
          <Button
            classes={{ root: classes.createBtn, buttonLabel: classes.createBtnLabel }}
            text={`${t('create_company')} “${search}”`}
            onClick={() => {
              onCreate({ name: search });
              clearSearch();
            }}
          />
        );
      }}
      onSelect={(item) => onSelect(item as Company)}
      queryFactory={companiesQueryKeys.acList}
      renderOption={(item) => (
        <CompanyLookupCard inputSize={InputSize.medium} investment={investment} item={item} withContainer={false} />
      )}
      setLocalSearch={setLocalSearch}
    />
  );
};

export const CompanyLookup = withStyles(styles)(forwardRef(CompanyLookupComponent));
